import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, useMediaQuery, Chip, Stack, alpha } from "@mui/material"; // Import Chip and Stack
import { LoadingButton } from '@mui/lab'; // Import LoadingButton
import { useMutation } from '@tanstack/react-query'; // Import useMutation
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { makeRequest } from '../util/AxiosInstance';
import { TitlePlusText } from '../shared/TitlePlusText';
import { formatCurrency, formatDateFromApi, handleError } from '../util/functions'; // Import handleError
import { useTheme } from '@emotion/react';
import { ProjectStatus } from '../types/project-status.enum'; // Import ProjectStatus enum
import { toast } from 'sonner'; // Import toast
import { appStore } from '../zustand';
import { Warning2 } from 'iconsax-react';

// Helper function to get status chip
const getStatusChip = (status) => {
    switch (status) {
        case ProjectStatus.PROJECT_CREATED_BY_ADMIN:
            return <Chip label="Pending Payment" color="warning" size="small" />;
        case ProjectStatus.PROJECT_IN_PROGRESS:
            return <Chip label="In Progress" color="primary" size="small" />;
        case ProjectStatus.PROJECT_CANCELLED:
            return <Chip label="Cancelled" color="error" size="small" />;
        case ProjectStatus.PROJECT_CLOSED_BY_ADMIN:
            return <Chip label="Closed" color="default" size="small" />;
        case ProjectStatus.CUSTOMER_APPROVE_CANCELLATION:
            return <Chip label="Cancellation Pending" color="warning" size="small" />;
        case ProjectStatus.PROJECT_CANCELLED_BY_CUSTOMER:
            return <Chip label="Cancelled by You" color="error" size="small" />;
        default:
            return <Chip label="Unknown" size="small" />;
    }
};

const ProjectDetails = ({ project }) => {
    const theme = useTheme();
    const mode = appStore((state) => state.mode); // Get mode from Zustand store
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate(); // Hook for navigation
    const [currentStatus, setCurrentStatus] = useState(project.status);

    const [orders, setOrders] = useState([]);

    const getOrders = async () => {
        await makeRequest('GET', `/get-project-orders/${project.id}`).then(res => {
            let data = res.data?.data?.data ?? [];
            setOrders(data);
        }).catch(err => {
            console.log(err);
        });
    }

    useEffect(() => {
        getOrders();
    }, []);

    // --- Mutation for approving cancellation ---
    const approveCancellationMutation = useMutation({
        mutationFn: async () => await makeRequest('POST', `/projects/${project.id}/cancel/approve`),
        onSuccess: (data) => {
            toast.success(data?.data?.message || "Cancellation approved successfully!");
            setCurrentStatus(ProjectStatus.PROJECT_CANCELLED);
        },
        onError: (error) => {
            const msg = handleError(error, "Failed to approve cancellation.");
            toast.error(msg);
        }
    });

    // --- Event Handlers ---
    const handleApproveCancellation = () => {
        approveCancellationMutation.mutate();
    };

    const handleContactSupport = () => {
        navigate('/chat');
    };

    // --- Conditional Rendering Logic ---
    const showDates =
        currentStatus === ProjectStatus.PROJECT_IN_PROGRESS ||
        currentStatus === ProjectStatus.PROJECT_CLOSED_BY_ADMIN; // Also show for closed

    const isClosed = currentStatus === ProjectStatus.PROJECT_CLOSED_BY_ADMIN;
    const needsCancellationApproval = currentStatus === ProjectStatus.CUSTOMER_APPROVE_CANCELLATION;

    return (
        <Box p={{ xs: 1, sm: 2 }} style={{ display: "flex", flexDirection: "column", gap: 16, textTransform: "capitalize" }}>
            <TitlePlusText title="Description" text={project.description} />

            {/* Status Chip */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 1 }}>
                <Typography
                    fontWeight={500} variant="caption" className='useLightColor' textTransform={'uppercase'}
                    sx={{ minWidth: '100px' }}>Status</Typography>
                {getStatusChip(currentStatus)}
            </Box>

            {/* Conditional Dates */}
            {showDates && (
                <>
                    <TitlePlusText title={isClosed ? "Started On" : "Started At"} text={formatDateFromApi(project.created_at)} />
                    <TitlePlusText title={isClosed ? "Ended On" : "End Date"} text={formatDateFromApi(project.project_detail?.end_at)} />
                </>
            )}

            <TitlePlusText title="Duration" text={project.project_detail?.duration ? `${project.project_detail.duration} days` : 'N/A'} />
            <TitlePlusText title="Last Updated" text={formatDateFromApi(project.project_detail?.updated_at)} />

            {/* Cancellation Approval Buttons */}
            {needsCancellationApproval && (
                <Box mt={2} backgroundColor={alpha(theme.palette.warning.light, 0.1)} p={2} borderRadius={1}>
                    <Typography variant="subtitle1" display={'flex'} gap={1} alignItems={'center'} color="warning.main" gutterBottom>
                        <Warning2 />   {mode !== 'admin' ? 'Action Required' : 'Project Cancellation Pending'}
                    </Typography>
                    <Typography variant='body1' fontWeight={600} gutterBottom mt={2} mb={1}>
                        {mode == 'admin' ? 'Awaiting Customer Approval' : 'Approve Project Cancellation'}
                    </Typography>
                    <Typography variant='body2' mb={2}>
                        {mode === 'admin' ? <>
                            This project has been cancelled. Awaiting approval from the customer. Please contact the customer to approve the cancellation.
                        </> : <>
                            This project has been cancelled by admin. Please approve the cancellation to finalize the closure, or contact support for further assistance.
                        </>}
                    </Typography>
                    {mode !== 'admin' && <>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <LoadingButton
                                size='small'
                                variant="contained"
                                color="error"
                                onClick={handleApproveCancellation}
                                loading={approveCancellationMutation.isPending}
                            >
                                Approve
                            </LoadingButton>
                            <Button
                                size='small'
                                variant="outlined"
                                onClick={handleContactSupport}
                            >
                                Contact Support
                            </Button>
                        </Stack>
                    </>}
                </Box>
            )}

            {/* Orders Section */}
            <Typography variant="h6" mt={needsCancellationApproval ? 0 : 2} mb={-1}>
                Orders
            </Typography>
            <Box>
                <table className='use-table' style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th>Amount</th>
                            <th>Status</th>
                            {!isMobile &&
                                <>
                                    <th>Created At</th>
                                    <th></th>
                                </>
                            }
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders && orders.length > 0 ?
                            orders
                                .filter((order) => order.status !== '3')
                                .map((order, index) => {
                                    const amount = parseFloat(order.amount);
                                    let orderStatus = 'Unpaid'
                                    let color = 'default'
                                    switch (order.status) {
                                        case "1":
                                            orderStatus = "Paid";
                                            color = 'success';
                                            break;
                                        case "0":
                                            orderStatus = "Unpaid";
                                            color = 'default';
                                            break;
                                        case "3":
                                            orderStatus = "Cancelled";
                                            color = 'error';
                                            break;
                                        default:
                                            orderStatus = "Unknown";
                                            break;
                                    }
                                    const amountError = isNaN(amount) || amount <= 0 ? 'Invalid amount' : '';

                                    return (
                                        <tr key={index + 1}>
                                            <td>
                                                {amountError ? <span style={{ color: 'red' }}>{amountError}</span> : formatCurrency(amount)}
                                            </td>
                                            <td>
                                                <Chip label={orderStatus} color={color} size="small" />
                                            </td>
                                            {!isMobile && <td colSpan={2}>{formatDateFromApi(order.created_at)}</td>}
                                            <td>
                                                {orderStatus !== 'Paid' && (
                                                    <Button
                                                        variant='text' color='secondary' size="small"
                                                        disabled={
                                                            orderStatus === 'Paid' ||
                                                            isClosed || mode == 'admin'
                                                            || currentStatus !== ProjectStatus.PROJECT_CREATED_BY_ADMIN
                                                        }
                                                    >
                                                        Pay
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                <tr>
                                    <td colSpan={isMobile ? 3 : 5} style={{ textAlign: 'center', padding: '16px' }}>
                                        <Typography>No orders available</Typography>
                                    </td>
                                </tr>
                            )}
                    </tbody>
                </table>
            </Box>

        </Box>
    );
};

export default ProjectDetails;